import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Sprout } from 'lucide-react';
import nurseryService from '../../services/nursery';
import LotPlaceFilter from '../../components/nursery/LotPlaceFilter';
import { slotWhere, stockItemLabel } from '../../utils/nurseryRowSlots';
import type {
  NurseryLocation, NurseryStockItem, LotStage, CreateLotPayload, PlaceFilter,
} from '../../types/nursery';

const STAGES: LotStage[] = ['sown', 'seedling', 'transplanted', 'grafted', 'budded', 'growing', 'potted', 'ready'];

/** Names only what the farm / block / row choices above have not already said. */
function locationLabel(l: NurseryLocation, place: PlaceFilter): string {
  if (l.location_type === 'nursery_row' && l.row_number != null) {
    if (place.row_number) return `Position ${l.sequence_number}`;
    const where = slotWhere({ ...l, block_name: place.block_id ? null : l.block_name });
    return place.farm_id || !l.farm_name ? where : `${l.farm_name} · ${where}`;
  }
  return `${l.name} (${l.location_type.replace('_', ' ')})`;
}

function inPlace(l: NurseryLocation, place: PlaceFilter): boolean {
  if (place.farm_id && l.farm_id !== place.farm_id) return false;
  if (place.block_id && l.block_id !== place.block_id) return false;
  if (place.row_number) {
    if (l.row_number !== place.row_number) return false;
    if ((l.row_side ?? 'none') !== (place.row_side ?? 'none')) return false;
  }
  return true;
}

function LotCreate() {
  const navigate = useNavigate();
  // /nursery/lots/new?location_id=N arrives with the place already chosen.
  const [searchParams] = useSearchParams();
  const presetLocationId = Number(searchParams.get('location_id')) || null;
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [stockItemsLoaded, setStockItemsLoaded] = useState(false);
  const [stockItemId, setStockItemId] = useState<number | ''>('');
  const [place, setPlace] = useState<PlaceFilter>({});
  const [locations, setLocations] = useState<NurseryLocation[]>([]);

  const [form, setForm] = useState<CreateLotPayload>({
    cultivar_id: 0, tree_type_id: 0, current_stage: 'sown', initial_count: 0, record_origin: true,
    ...(presetLocationId ? { location_id: presetLocationId } : {}),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getStockItems({ active: true }).then(r => {
      if (r.success) setStockItems(r.data || []);
      setStockItemsLoaded(true);
    });
    nurseryService.getLocations({ active: true }).then(r => {
      if (!r.success) return;
      setLocations(r.data || []);
      const preset = (r.data || []).find(l => l.id === presetLocationId);
      if (preset) {
        setPlace({
          farm_id: preset.farm_id ?? undefined,
          block_id: preset.block_id ?? undefined,
          ...(preset.row_number ? { row_number: preset.row_number, row_side: preset.row_side ?? 'none' } : {}),
        });
      }
    });
  }, [presetLocationId]);

  const placeLocations = useMemo(() => locations.filter(l => inPlace(l, place)), [locations, place]);

  // Only a stock item can be placed: the order form sells stock items, so a
  // lot of anything else is trees nobody can order. The lot records the item's
  // cultivar, rootstock and tree type.
  const chooseStockItem = (id: number | '') => {
    setStockItemId(id);
    const item = stockItems.find(i => i.id === id);
    setForm(f => ({ ...f, cultivar_id: item?.cultivar_id ?? 0, rootstock_id: item?.rootstock_id ?? null, tree_type_id: item?.tree_type_id ?? 0 }));
  };

  // Narrowing the place drops a chosen location that is no longer inside it.
  const choosePlace = (next: PlaceFilter) => {
    setPlace(next);
    setForm(f => {
      const chosen = locations.find(l => l.id === f.location_id);
      return chosen && !inPlace(chosen, next) ? { ...f, location_id: null } : f;
    });
  };

  const set = (patch: Partial<CreateLotPayload>) => setForm(f => ({ ...f, ...patch }));

  const submit = async () => {
    if (!stockItemId) { setError('Choose a stock item'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await nurseryService.createLot(form);
      if (res.success && res.data) navigate(`/nursery/lots/${res.data.id}`);
      else setError((res as { error?: { message?: string } }).error?.message || 'Failed to create lot');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader title="Place a Lot" subtitle="Put stock into a location" icon={<Sprout className="h-6 w-6 text-white" />} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
          <div>
            <label htmlFor="lot-stock-item" className="block text-sm font-medium text-neutral-700 mb-1">Stock item <span className="text-red-500">*</span></label>
            <select id="lot-stock-item" value={stockItemId}
              onChange={e => chooseStockItem(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Choose a stock item…</option>
              {stockItems.map(i => <option key={i.id} value={i.id}>{stockItemLabel(i)}</option>)}
            </select>
            <p className="text-xs text-neutral-400 mt-1 break-words">
              {stockItemsLoaded && stockItems.length === 0 ? 'No stock items yet. ' : 'Only stock items can be placed. Growing something new? '}
              <Link to="/nursery/stock-items/new" className="text-primary-700 hover:underline">Add a stock item</Link>
            </p>
          </div>

          <div>
            <label htmlFor="lot-stage" className="block text-sm font-medium text-neutral-700 mb-1">Stage</label>
            <select id="lot-stage" value={form.current_stage} onChange={e => set({ current_stage: e.target.value as LotStage })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <p className="block text-sm font-medium text-neutral-700 mb-1">Where</p>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <LotPlaceFilter value={place} onChange={choosePlace} allLabel="Any" />
            </div>
            <label htmlFor="lot-location" className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
            <select id="lot-location" value={form.location_id ?? ''} onChange={e => set({ location_id: e.target.value ? Number(e.target.value) : null })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Unassigned</option>
              {placeLocations.map(l => <option key={l.id} value={l.id}>{locationLabel(l, place)}</option>)}
            </select>
            <p className="text-xs text-neutral-400 mt-1">
              {placeLocations.length} location{placeLocations.length === 1 ? '' : 's'} to choose from. Pick a farm, block and row above to narrow them.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Count</label>
              <input type="number" min="0" value={form.initial_count ?? 0}
                onChange={e => set({ initial_count: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Plant / sow date</label>
              <input type="date" value={form.plant_date ?? ''} onChange={e => set({ plant_date: e.target.value || null })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Source / supplier (provenance)</label>
            <input value={form.source_supplier ?? ''} onChange={e => set({ source_supplier: e.target.value || null })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. seed from XYZ Seeds" />
          </div>

          <p className="text-xs text-neutral-400">
            The ready date is computed automatically from the tree type's readiness rule once dates are set.
          </p>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/lots')}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submit} disabled={submitting}>
              {submitting ? 'Placing…' : 'Place lot'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default LotCreate;
